import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import LayoutSubmaster from "../../components/layoutSubmasterConfig/layoutSubmaster";
import LocalStorage from "../../helpers/localstorage";
import { apiClientSubmaster } from "../../services/setupClientSubmaster/apiClientSubmaster";

type StockCompany = {
  value: string;
  name: string;
  cnpj: string;
  is_main: boolean;
};

type StockRow = {
  id: number;
  description: string;
  observation?: string | null;
  is_default: boolean;
  submaster_company_id?: number | null;
  company_name: string;
  company_cnpj: string;
  created_at: string;
  total_quantity: number;
  epi_count: number;
};

type StockEpi = {
  id: number;
  epi_name: string;
  ca?: string | null;
  internal_code?: string | null;
  quantity: number;
  average_price?: string | null;
  active: boolean;
};

type StockEntry = {
  id: number;
  movement_key: string;
  movement_type: "ENTRY" | "RETURN";
  quantity: number;
  unit_price: string | null;
  average_price_after: string | null;
  entry_date: string;
  epi_name: string;
  ca?: string | null;
  stock_description: string;
};

type StockInventoryItem = {
  id: number;
  epi_name: string;
  ca?: string | null;
  internal_code?: string | null;
  active: boolean;
  average_price?: string | null;
  stock_quantity: number;
  total_quantity: number;
};

type FormMode = "create" | "edit";
type StockMovementType = "ENTRY" | "RETURN";

const formatCnpj = (value: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : value || "Não informado";
};

const formatCurrency = (value?: string | number | null) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value: string) => {
  const datePart = String(value || "").slice(0, 10);
  const [year, month, day] = datePart.split("-");
  return year && month && day ? `${day}/${month}/${year}` : "-";
};

function ConfigSubmasterStockPage() {
  const storage = useMemo(() => new LocalStorage(), []);
  const apiClient = useMemo(
    () => apiClientSubmaster(String(storage.getAuthToken() || "")),
    [storage]
  );
  const [stocks, setStocks] = useState<StockRow[]>([]);
  const [companies, setCompanies] = useState<StockCompany[]>([]);
  const [epis, setEpis] = useState<StockEpi[]>([]);
  const [entries, setEntries] = useState<StockEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [description, setDescription] = useState("");
  const [observation, setObservation] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [formMode, setFormMode] = useState<FormMode>("create");
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [editingDefaultStock, setEditingDefaultStock] = useState(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [entryMode, setEntryMode] = useState<StockMovementType>("ENTRY");
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryEpiId, setEntryEpiId] = useState("");
  const [entryQuantity, setEntryQuantity] = useState("");
  const [entryUnitPrice, setEntryUnitPrice] = useState("");
  const [entryStockId, setEntryStockId] = useState("");
  const [entryDate, setEntryDate] = useState(getToday());
  const [selectedInventoryStock, setSelectedInventoryStock] = useState<StockRow | null>(null);
  const [stockItems, setStockItems] = useState<StockInventoryItem[]>([]);
  const [loadingStockItems, setLoadingStockItems] = useState(false);
  const [editingStockItem, setEditingStockItem] = useState<StockInventoryItem | null>(null);
  const [newStockQuantity, setNewStockQuantity] = useState("");
  const [stockAdjustmentObservation, setStockAdjustmentObservation] = useState("");
  const [savingStockAdjustment, setSavingStockAdjustment] = useState(false);
  const canStockEntry = storage.hasWebPermission("canStockEntry");
  const canStockEdit = storage.hasWebPermission("canStockEdit");
  const canStockDelete = storage.hasWebPermission("canStockDelete");

  const loadStocks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.post("/stocks/list", {});
      const nextStocks = Array.isArray(response?.data?.data) ? response.data.data : [];
      const nextCompanies = Array.isArray(response?.data?.companies)
        ? response.data.companies
        : [];
      const nextEpis = Array.isArray(response?.data?.epis) ? response.data.epis : [];
      const nextEntries = Array.isArray(response?.data?.entries) ? response.data.entries : [];

      setPermissionDenied(false);
      setStocks(nextStocks);
      setCompanies(nextCompanies);
      setEpis(nextEpis);
      setEntries(nextEntries);
      setCompanyId((current) => {
        if (nextCompanies.length === 1) {
          return String(nextCompanies[0].value);
        }
        return nextCompanies.some((company: StockCompany) => String(company.value) === current)
          ? current
          : "";
      });
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setPermissionDenied(true);
        return;
      }
      toast.error("Não foi possível carregar os estoques.");
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  const openCreateForm = () => {
    setShowEntryForm(false);
    setSelectedInventoryStock(null);
    setFormMode("create");
    setEditingStockId(null);
    setEditingDefaultStock(false);
    setDescription("");
    setObservation("");
    setCompanyId(companies.length === 1 ? String(companies[0].value) : "");
    setShowForm(true);
  };

  const openEditForm = (stock: StockRow) => {
    setShowEntryForm(false);
    setSelectedInventoryStock(null);
    setFormMode("edit");
    setEditingStockId(stock.id);
    setEditingDefaultStock(stock.is_default);
    setDescription(stock.description);
    setObservation(stock.observation || "");
    setCompanyId(
      stock.submaster_company_id === null || stock.submaster_company_id === undefined
        ? "main"
        : String(stock.submaster_company_id)
    );
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEntryForm = (stockId?: number, movementType: StockMovementType = "ENTRY") => {
    setShowForm(false);
    setSelectedInventoryStock(null);
    setEntryMode(movementType);
    setEntryEpiId("");
    setEntryQuantity("");
    setEntryUnitPrice("");
    setEntryStockId(stockId ? String(stockId) : stocks.length === 1 ? String(stocks[0].id) : "");
    setEntryDate(getToday());
    setShowEntryForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadStockItems = async (stock: StockRow) => {
    try {
      setLoadingStockItems(true);
      const response = await apiClient.post("/stocks/items", { stockId: stock.id });
      if (response?.data?.res !== true) {
        throw new Error(response?.data?.message || "Não foi possível carregar os EPIs do estoque.");
      }
      setSelectedInventoryStock(stock);
      setStockItems(Array.isArray(response?.data?.data) ? response.data.data : []);
      setEditingStockItem(null);
      setShowForm(false);
      setShowEntryForm(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Não foi possível carregar os EPIs do estoque."
      );
    } finally {
      setLoadingStockItems(false);
    }
  };

  const openStockQuantityAdjustment = (item: StockInventoryItem) => {
    setEditingStockItem(item);
    setNewStockQuantity(String(item.stock_quantity || 0));
    setStockAdjustmentObservation("");
  };

  const handleSaveStockAdjustment = async () => {
    if (!selectedInventoryStock || !editingStockItem) return;
    const quantity = Number(newStockQuantity);

    if (!Number.isInteger(quantity) || quantity < 0) {
      toast.warning("Informe uma quantidade inteira igual ou maior que zero.");
      return;
    }

    const confirmed = window.confirm(
      `Confirma o saldo de ${quantity} unidade(s) para o EPI "${editingStockItem.epi_name}"?\n\n` +
      `Empresa: ${selectedInventoryStock.company_name}\n` +
      `Estoque: ${selectedInventoryStock.description}`
    );

    if (!confirmed) return;

    try {
      setSavingStockAdjustment(true);
      const response = await apiClient.post("/stocks/balance/update", {
        stockId: selectedInventoryStock.id,
        epiId: editingStockItem.id,
        newQuantity: quantity,
        observation: stockAdjustmentObservation.trim(),
      });
      if (response?.data?.res !== true) {
        throw new Error(response?.data?.message || "Não foi possível alterar o saldo.");
      }

      toast.success("Saldo do EPI atualizado neste estoque.");
      setEditingStockItem(null);
      await Promise.all([loadStocks(), loadStockItems(selectedInventoryStock)]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Não foi possível alterar o saldo."
      );
    } finally {
      setSavingStockAdjustment(false);
    }
  };

  const selectedEpi = useMemo(
    () => epis.find((epi) => String(epi.id) === entryEpiId),
    [entryEpiId, epis]
  );

  const selectedEntryStock = useMemo(
    () => stocks.find((stock) => String(stock.id) === entryStockId),
    [entryStockId, stocks]
  );

  const handleSaveEntry = async () => {
    const quantity = Number(entryQuantity);
    const unitPrice = Number(entryUnitPrice);

    if (!entryEpiId) {
      toast.warning("Selecione o EPI.");
      return;
    }
    if (!Number.isInteger(quantity) || quantity <= 0) {
      toast.warning("Informe uma quantidade inteira maior que zero.");
      return;
    }
    if (
      entryMode === "ENTRY" &&
      (!Number.isFinite(unitPrice) || unitPrice < 0 || entryUnitPrice === "")
    ) {
      toast.warning("Informe um preço válido.");
      return;
    }
    if (!entryStockId) {
      toast.warning("Selecione o estoque de destino.");
      return;
    }
    if (!entryDate) {
      toast.warning("Informe a data de entrada.");
      return;
    }

    try {
      setSavingEntry(true);
      const response = await apiClient.post("/stocks/entries/create", {
        epiId: Number(entryEpiId),
        quantity,
        unitPrice: entryMode === "ENTRY" ? unitPrice : undefined,
        stockId: Number(entryStockId),
        entryDate,
        movementType: entryMode,
      });

      if (response?.data?.res !== true) {
        throw new Error(response?.data?.message || "Não foi possível registrar a entrada.");
      }

      const averagePrice = response?.data?.data?.average_price;
      toast.success(
        entryMode === "RETURN"
          ? `Devolução registrada. Preço médio mantido em ${formatCurrency(averagePrice)}.`
          : `Entrada registrada. Novo preço médio: ${formatCurrency(averagePrice)}.`
      );
      setShowEntryForm(false);
      await loadStocks();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Não foi possível registrar a entrada."
      );
    } finally {
      setSavingEntry(false);
    }
  };

  const handleSaveStock = async () => {
    if (!description.trim()) {
      toast.warning("Informe a descrição do estoque.");
      return;
    }
    if (!companyId) {
      toast.warning("Selecione a empresa do estoque.");
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.post(
        formMode === "edit" ? "/stocks/update" : "/stocks/create",
        {
        stockId: formMode === "edit" ? editingStockId : undefined,
        description: description.trim(),
        observation: observation.trim(),
        companyId,
        }
      );

      if (response?.data?.res !== true) {
        throw new Error(response?.data?.message || "Não foi possível salvar o estoque.");
      }

      toast.success(
        formMode === "edit" ? "Estoque atualizado com sucesso." : "Estoque criado com sucesso."
      );
      setShowForm(false);
      await loadStocks();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Não foi possível salvar o estoque."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStock = async (stock: StockRow) => {
    if (stock.is_default) return toast.warning("O estoque padrão não pode ser excluído.");
    if (!window.confirm(`Excluir o estoque "${stock.description}"?`)) return;
    try {
      const response = await apiClient.post("/stocks/delete", { stockId: stock.id });
      if (response?.data?.res !== true) throw new Error(response?.data?.message);
      toast.success("Estoque excluído.");
      await loadStocks();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Não foi possível excluir o estoque.");
    }
  };

  const columns: TableColumn<StockRow>[] = [
    {
      name: "Descrição",
      selector: (row) => row.description,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="py-2">
          <strong className="d-block">{row.description}</strong>
          {row.is_default && <span className="badge bg-primary mt-1">Estoque padrão</span>}
        </div>
      ),
    },
    {
      name: "Empresa",
      selector: (row) => row.company_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="py-2">
          <span className="d-block">{row.company_name}</span>
          <small className="text-muted">{formatCnpj(row.company_cnpj)}</small>
        </div>
      ),
    },
    {
      name: "Observação",
      selector: (row) => row.observation || "",
      grow: 3,
      cell: (row) => row.observation || <span className="text-muted">Sem observação</span>,
    },
    {
      name: "Saldo",
      selector: (row) => Number(row.total_quantity || 0),
      sortable: true,
      width: "105px",
      cell: (row) => (
        <div className="py-2 text-center">
          <strong className="d-block">{Number(row.total_quantity || 0)}</strong>
          <small className="text-muted">{Number(row.epi_count || 0)} EPI(s)</small>
        </div>
      ),
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex flex-nowrap gap-2 py-2">
          {(canStockEntry || canStockEdit) && <button
            type="button"
            className="btn btn-outline-dark btn-sm text-nowrap d-inline-flex align-items-center"
            onClick={() => loadStockItems(row)}
            title="Visualizar os EPIs e saldos deste estoque"
          >
            <i className="bi bi-list-check me-1" /> EPIs
          </button>}
          {canStockEntry && <button
            type="button"
            className="btn btn-outline-success btn-sm text-nowrap d-inline-flex align-items-center"
            onClick={() => openEntryForm(row.id)}
            title="Registrar entrada neste estoque"
          >
            <i className="bi bi-box-arrow-in-down me-1" /> Entrada
          </button>}
          {canStockDelete && !row.is_default && <button
            type="button"
            className="btn btn-outline-danger btn-sm text-nowrap"
            onClick={() => handleDeleteStock(row)}
            title="Excluir estoque sem saldo ou histórico"
          >
            <i className="bi bi-trash-fill me-1" /> Excluir
          </button>}
          {canStockEdit && <button
            type="button"
            className="btn btn-outline-primary btn-sm text-nowrap d-inline-flex align-items-center justify-content-center px-3"
            style={{ width: "88px", minWidth: "88px", height: "38px", borderRadius: "10px" }}
            onClick={() => openEditForm(row)}
            title="Editar estoque"
          >
            <i className="bi bi-pencil-fill me-1" /> Editar
          </button>}
        </div>
      ),
      width: "390px",
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  const stockItemColumns: TableColumn<StockInventoryItem>[] = [
    {
      name: "EPI",
      selector: (row) => row.epi_name,
      sortable: true,
      grow: 3,
      minWidth: "210px",
      cell: (row) => (
        <div
          className="py-2"
          style={{ whiteSpace: "normal", wordBreak: "normal", overflowWrap: "normal" }}
        >
          <strong className="d-block">{row.epi_name}</strong>
          <small className="text-muted">
            {row.ca ? `CA: ${row.ca}` : "Sem CA"}
            {!row.active ? " • Inativo" : ""}
          </small>
        </div>
      ),
    },
    {
      name: "Código interno",
      selector: (row) => row.internal_code || "",
      sortable: true,
      width: "110px",
    },
    {
      name: "Saldo neste estoque",
      selector: (row) => Number(row.stock_quantity || 0),
      sortable: true,
      center: true,
      width: "105px",
      cell: (row) => <strong>{Number(row.stock_quantity || 0)}</strong>,
    },
    {
      name: "Saldo em todos os estoques",
      selector: (row) => Number(row.total_quantity || 0),
      sortable: true,
      center: true,
      width: "125px",
    },
    {
      name: "Preço médio",
      selector: (row) => Number(row.average_price || 0),
      sortable: true,
      width: "130px",
      cell: (row) => formatCurrency(row.average_price),
    },
    {
      name: "Ação",
      width: "165px",
      cell: (row) => (
        canStockEdit ? <button
          type="button"
          className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center text-nowrap px-3"
          style={{ minWidth: "135px", height: "38px", borderRadius: "10px" }}
          onClick={() => openStockQuantityAdjustment(row)}
        >
          <i className="bi bi-sliders me-1" /> Alterar saldo
        </button> : <span className="text-muted">Somente leitura</span>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  const entryColumns: TableColumn<StockEntry>[] = [
    {
      name: "Data",
      selector: (row) => row.entry_date,
      sortable: true,
      width: "105px",
      cell: (row) => formatDate(row.entry_date),
    },
    {
      name: "Tipo",
      selector: (row) => row.movement_type,
      sortable: true,
      width: "125px",
      cell: (row) => (
        <span className={`badge ${row.movement_type === "RETURN" ? "bg-warning text-dark" : "bg-success"}`}>
          {row.movement_type === "RETURN" ? "Devolução" : "Entrada"}
        </span>
      ),
    },
    {
      name: "EPI",
      selector: (row) => row.epi_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="py-2">
          <strong className="d-block">{row.epi_name}</strong>
          {row.ca && <small className="text-muted">CA: {row.ca}</small>}
        </div>
      ),
    },
    {
      name: "Estoque",
      selector: (row) => row.stock_description,
      sortable: true,
      grow: 2,
    },
    {
      name: "Quantidade",
      selector: (row) => row.quantity,
      sortable: true,
      width: "115px",
      center: true,
    },
    {
      name: "Preço unitário",
      selector: (row) => Number(row.unit_price),
      sortable: true,
      width: "135px",
      cell: (row) => row.movement_type === "RETURN" ? "—" : formatCurrency(row.unit_price),
    },
    {
      name: "Preço médio",
      selector: (row) => Number(row.average_price_after),
      sortable: true,
      width: "130px",
      cell: (row) => row.movement_type === "RETURN" ? "Não altera" : formatCurrency(row.average_price_after),
    },
  ];

  return (
    <LayoutSubmaster>
      <div className="report-config-page management-page">
        <section className="dashboard-hero report-hero mb-4">
          <div className="report-hero__content">
            <span className="pill-badge">Estoque</span>
            <h1 className="dashboard-title">Controle de estoque</h1>
            <p className="dashboard-subtitle">
              Cadastre os estoques e defina a empresa responsável por cada um.
            </p>
          </div>
          {!permissionDenied && (
            <div className="d-flex flex-wrap gap-2">
              {canStockEntry && <button className="btn btn-success epi-primary-action" onClick={() => openEntryForm()}>
                <i className="bi bi-box-arrow-in-down me-2" /> Entrada de estoque
              </button>}
              {canStockEntry && <button
                className="btn btn-warning epi-primary-action"
                onClick={() => openEntryForm(undefined, "RETURN")}
              >
                <i className="bi bi-arrow-return-left me-2" /> Devolução
              </button>}
              {canStockEdit && <button className="btn btn-primary epi-primary-action" onClick={openCreateForm}>
                <i className="bi bi-plus-circle me-2" /> Criar estoque
              </button>}
            </div>
          )}
        </section>

        {permissionDenied ? (
          <div className="alert alert-warning">
            A permissão <strong>Controla Estoque</strong> não está habilitada para esta empresa.
          </div>
        ) : (
          <>
            {showEntryForm && (
              <section className="section-panel report-filter-panel mb-4">
                <div className="section-heading">
                  <div>
                    <h3>{entryMode === "RETURN" ? "Devolução de EPI" : "Entrada de estoque"}</h3>
                    <p>
                      {entryMode === "RETURN"
                        ? "Selecione o EPI devolvido. Esta movimentação não altera o preço médio."
                        : "Selecione o EPI e informe os dados da entrada."}
                    </p>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label" htmlFor="stock-entry-epi">EPI</label>
                    <select
                      id="stock-entry-epi"
                      className="form-select"
                      value={entryEpiId}
                      onChange={(event) => setEntryEpiId(event.target.value)}
                    >
                      <option value="">Selecione o EPI</option>
                      {epis.map((epi) => (
                        <option value={epi.id} key={epi.id}>
                          {epi.epi_name}
                          {epi.ca ? ` - CA ${epi.ca}` : ""}
                          {!epi.active ? " (inativo)" : ""}
                        </option>
                      ))}
                    </select>
                    {epis.length === 0 && (
                      <small className="text-muted">Nenhum EPI cadastrado.</small>
                    )}
                  </div>

                  {selectedEpi && (
                    <>
                      <div className={entryMode === "RETURN" ? "col-md-4" : "col-md-3"}>
                        <label className="form-label" htmlFor="stock-entry-quantity">Quantidade</label>
                        <input
                          id="stock-entry-quantity"
                          type="number"
                          min="1"
                          step="1"
                          className="form-control"
                          value={entryQuantity}
                          onChange={(event) => setEntryQuantity(event.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {entryMode === "ENTRY" && (<div className="col-md-3">
                        <label className="form-label" htmlFor="stock-entry-price">Preço unitário</label>
                        <div className="input-group">
                          <span className="input-group-text">R$</span>
                          <input
                            id="stock-entry-price"
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={entryUnitPrice}
                            onChange={(event) => setEntryUnitPrice(event.target.value)}
                            placeholder="0,00"
                          />
                        </div>
                      </div>)}
                      <div className={entryMode === "RETURN" ? "col-md-4" : "col-md-3"}>
                        <label className="form-label" htmlFor="stock-entry-stock">Estoque</label>
                        <select
                          id="stock-entry-stock"
                          className="form-select"
                          value={entryStockId}
                          onChange={(event) => setEntryStockId(event.target.value)}
                        >
                          <option value="">Selecione o estoque</option>
                          {stocks.map((stock) => (
                            <option value={stock.id} key={stock.id}>
                              {stock.company_name} — {stock.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={entryMode === "RETURN" ? "col-md-4" : "col-md-3"}>
                        <label className="form-label" htmlFor="stock-entry-date">
                          {entryMode === "RETURN" ? "Data da devolução" : "Data de entrada"}
                        </label>
                        <input
                          id="stock-entry-date"
                          type="date"
                          className="form-control"
                          value={entryDate}
                          onChange={(event) => setEntryDate(event.target.value)}
                        />
                      </div>
                      <div className="col-12">
                        <div className="alert alert-light border mb-0 py-2">
                          Empresa de destino: <strong>{selectedEntryStock?.company_name || "Selecione o estoque"}</strong>
                          <span className="mx-2">•</span>
                          Estoque: <strong>{selectedEntryStock?.description || "-"}</strong>
                          <br />
                          Saldo total atual: <strong>{Number(selectedEpi.quantity || 0)}</strong>
                          <span className="mx-2">•</span>
                          Preço médio atual: <strong>{formatCurrency(selectedEpi.average_price)}</strong>
                          {entryMode === "RETURN" && (
                            <span className="ms-2 text-success">(será mantido)</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={savingEntry}
                      onClick={() => setShowEntryForm(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={savingEntry || !selectedEpi}
                      onClick={handleSaveEntry}
                    >
                      {savingEntry
                        ? "Registrando..."
                        : entryMode === "RETURN"
                          ? "Registrar devolução"
                          : "Registrar entrada"}
                    </button>
                  </div>
                </div>
              </section>
            )}

            {showForm && (
              <section className="section-panel report-filter-panel mb-4">
                <div className="section-heading">
                  <div>
                    <h3>{formMode === "edit" ? "Editar estoque" : "Novo estoque"}</h3>
                    <p>
                      {formMode === "edit"
                        ? "Altere a descrição e a observação do estoque."
                        : "Informe os dados e a empresa associada."}
                    </p>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-lg-6">
                    <label className="form-label" htmlFor="stock-description">Descrição</label>
                    <input
                      id="stock-description"
                      className="form-control"
                      value={description}
                      maxLength={255}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="Ex.: Almoxarifado principal"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label" htmlFor="stock-company">Empresa</label>
                    <select
                      id="stock-company"
                      className="form-select"
                      value={companyId}
                      disabled={companies.length === 1 || editingDefaultStock}
                      onChange={(event) => setCompanyId(event.target.value)}
                    >
                      {companies.length > 1 && <option value="">Selecione a empresa</option>}
                      {companies.map((company) => (
                        <option value={company.value} key={company.value}>
                          {company.name} - {formatCnpj(company.cnpj)}
                          {company.is_main ? " (principal)" : ""}
                        </option>
                      ))}
                    </select>
                    {editingDefaultStock && (
                      <small className="text-muted">
                        A empresa do estoque padrão é mantida automaticamente.
                      </small>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label" htmlFor="stock-observation">Observação</label>
                    <textarea
                      id="stock-observation"
                      className="form-control"
                      rows={3}
                      maxLength={2000}
                      value={observation}
                      onChange={(event) => setObservation(event.target.value)}
                      placeholder="Informações adicionais sobre este estoque"
                    />
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={saving}
                      onClick={() => setShowForm(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={saving}
                      onClick={handleSaveStock}
                    >
                      {saving
                        ? "Salvando..."
                        : formMode === "edit"
                          ? "Salvar alterações"
                          : "Salvar estoque"}
                    </button>
                  </div>
                </div>
              </section>
            )}

            <section className="section-panel">
              <div className="section-heading">
                <div>
                  <h3>Estoques cadastrados</h3>
                  <p>Lista de estoques separados por empresa.</p>
                </div>
                <span className="pill-badge">{stocks.length} estoque(s)</span>
              </div>
              <DataTable
                columns={columns}
                data={stocks}
                progressPending={loading}
                pagination
                responsive
                highlightOnHover
                noDataComponent="Nenhum estoque cadastrado."
                progressComponent={<div className="p-4">Carregando estoques...</div>}
              />
            </section>

            {selectedInventoryStock && (
              <section className="section-panel mt-4">
                <div className="section-heading">
                  <div>
                    <span className="pill-badge mb-2">Estoque por empresa</span>
                    <h3>EPIs — {selectedInventoryStock.description}</h3>
                    <div className="alert alert-primary py-2 px-3 mb-2 d-inline-block">
                      Empresa: <strong>{selectedInventoryStock.company_name}</strong>
                      <span className="mx-2">•</span>
                      CNPJ: <strong>{formatCnpj(selectedInventoryStock.company_cnpj)}</strong>
                    </div>
                    <p>
                      Cada quantidade abaixo pertence somente a este estoque. EPIs sem movimentação aparecem com saldo zero.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center text-nowrap px-3 flex-shrink-0"
                    style={{ width: "90px", minWidth: "90px", height: "38px", borderRadius: "10px" }}
                    onClick={() => {
                      setSelectedInventoryStock(null);
                      setEditingStockItem(null);
                    }}
                  >
                    <i className="bi bi-x-lg me-1" /> Fechar
                  </button>
                </div>

                {editingStockItem && (
                  <div className="border rounded p-3 mb-4 bg-light">
                    <div className="mb-3">
                      <strong>{editingStockItem.epi_name}</strong>
                      <span className="text-muted ms-2">
                        Saldo atual neste estoque: {Number(editingStockItem.stock_quantity || 0)}
                      </span>
                      <div className="text-primary mt-2">
                        Alterando somente o estoque da empresa <strong>{selectedInventoryStock.company_name}</strong>.
                      </div>
                    </div>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-3">
                        <label className="form-label" htmlFor="stock-adjustment-quantity">
                          Nova quantidade
                        </label>
                        <input
                          id="stock-adjustment-quantity"
                          type="number"
                          min="0"
                          step="1"
                          className="form-control"
                          value={newStockQuantity}
                          onChange={(event) => setNewStockQuantity(event.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" htmlFor="stock-adjustment-observation">
                          Motivo ou observação
                        </label>
                        <input
                          id="stock-adjustment-observation"
                          type="text"
                          maxLength={2000}
                          className="form-control"
                          value={stockAdjustmentObservation}
                          onChange={(event) => setStockAdjustmentObservation(event.target.value)}
                          placeholder="Ex.: contagem física do estoque"
                        />
                      </div>
                      <div className="col-md-3 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={savingStockAdjustment}
                          onClick={() => setEditingStockItem(null)}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={savingStockAdjustment}
                          onClick={handleSaveStockAdjustment}
                        >
                          {savingStockAdjustment ? "Salvando..." : "Salvar saldo"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <DataTable
                  columns={stockItemColumns}
                  data={stockItems}
                  progressPending={loadingStockItems}
                  pagination
                  paginationPerPage={20}
                  responsive
                  highlightOnHover
                  noDataComponent="Nenhum EPI cadastrado."
                  progressComponent={<div className="p-4">Carregando EPIs do estoque...</div>}
                />
              </section>
            )}

            <section className="section-panel mt-4">
              <div className="section-heading">
                <div>
                  <h3>Últimas movimentações</h3>
                  <p>Histórico das 100 entradas e devoluções mais recentes.</p>
                </div>
                <span className="pill-badge">{entries.length} movimentação(ões)</span>
              </div>
              <DataTable
                columns={entryColumns}
                data={entries}
                keyField="movement_key"
                pagination
                responsive
                highlightOnHover
                noDataComponent="Nenhuma movimentação de estoque registrada."
              />
            </section>
          </>
        )}
      </div>
    </LayoutSubmaster>
  );
}

export default ConfigSubmasterStockPage;
